import { useEffect, useMemo } from 'react';

import { lodash } from 'helpers';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { Icon } from 'libs/ui/Icon';
import { useDirectoryContext } from 'modules/directory/common/providers/DirectoryProvider';
import { DirectoryRecordActionType } from 'modules/directory/directoryRecord/types';

type Args = {
  isCanEdit: boolean;
  onArchive?: () => void;
  onDearchive?: () => void;
};

export const useDirectoryRecordFormHeaderActions = ({
  isCanEdit,
  onArchive,
  onDearchive,
}: Args) => {
  const { t } = useTranslation();

  const { readOnly, setReadOnly, resetForm } = useFormContext();

  const { onEnableEdit } = useDirectoryContext();

  const menuActions = useMemo(
    () =>
      lodash.compact([
        isCanEdit && {
          value: DirectoryRecordActionType.edit,
          label: t('common.edit'),
          Icon: Icon.Edit,
        },
        onArchive && {
          value: DirectoryRecordActionType.archive,
          label: t('common.archive'),
          Icon: Icon.TimeSleep,
        },
        onDearchive && {
          value: DirectoryRecordActionType.dearchive,
          label: t('common.dearchive'),
          Icon: Icon.RotateRightCheck,
        },
      ]),
    [onArchive, onDearchive, isCanEdit, t]
  );

  const onClickAction = (type: DirectoryRecordActionType) => {
    switch (type) {
      case DirectoryRecordActionType.edit:
        onEnableEdit(() => setReadOnly(false));
        break;
      case DirectoryRecordActionType.archive:
        onArchive?.();
        break;
      case DirectoryRecordActionType.dearchive:
        onDearchive?.();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (readOnly) {
      resetForm();
    }
  }, [readOnly, resetForm]);

  return {
    menuActions,
    onClickAction,
  };
};
